import { Dialog } from "@base-ui/react/dialog";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { usePortalHost } from "../../providers/portal-host";
import { IconButton } from "../icon-button";

type ModalSize = "sm" | "md" | "lg";

const modalStyles = cva(
  [
    "w-[min(calc(100vw_-_(var(--spacing-16)_*_2)),var(--modal-size))]",
    "max-w-(--modal-size)",
    "min-w-0",
    "max-h-[calc(100vh_-_var(--spacing-32))]",
    "flex flex-col",
    "overflow-hidden",
    "rounded-(--radius-sm)",
    "bg-surface-container-lowest",
    "shadow-elevation-5",
    "p-(--spacing-20)",
    "outline-none",
    "max-[599px]:min-h-[230px]",
    "max-[599px]:max-h-[min(680px,calc(100dvh_-_var(--spacing-16)))]",
    "max-[599px]:w-full",
    "max-[599px]:max-w-none",
    "max-[599px]:rounded-b-none",
    "max-[599px]:pb-[calc(var(--spacing-20)_+_env(safe-area-inset-bottom))]",
  ],
  {
    variants: {
      size: {
        sm: "[--modal-size:320px]",
        md: "[--modal-size:600px]",
        lg: "[--modal-size:1100px]",
      },
    },
  },
);

const footerStyles = cva("shrink-0 mt-(--spacing-20) flex gap-(--spacing-16)", {
  variants: {
    size: {
      sm: ["h-auto", "flex-col-reverse", "items-stretch", "[&>*]:w-full"],
      md: "h-(--spacing-40) items-center justify-end",
      lg: "h-(--spacing-40) items-center justify-end",
    },
  },
});

const titleStyles = cva([
  "font-(family-name:--font-family-title)",
  "text-(length:--font-size-title-lg)",
  "font-(number:--font-weight-medium)",
  "leading-(--font-line-height-28)",
  "text-text-default",
]);

const descriptionStyles = cva([
  "text-(length:--font-size-body-medium)",
  "leading-(--font-line-height-20)",
  "text-text-subtext",
]);

export interface ModalProps {
  /** Whether the modal is open. */
  open: boolean;
  /** Called when the modal requests an open state change. */
  onOpenChange: (open: boolean) => void;
  /** Modal heading. Used as the dialog accessible name. */
  title: string;
  /** Optional supporting copy. Used as the dialog accessible description. */
  description?: string;
  /** Whether close button, Escape, and backdrop dismissal are enabled. */
  dismissible?: boolean;
  /** Modal target width and footer layout. Defaults to "md". */
  size?: ModalSize;
  /** Slot for modal body content. */
  children?: React.ReactNode;
  /** Slot for modal footer actions. */
  footer?: React.ReactNode;
}

function Modal({
  open,
  onOpenChange,
  title,
  description,
  dismissible = true,
  size,
  children,
  footer,
}: ModalProps) {
  const portalHost = usePortalHost();
  const resolvedSize = size ?? "md";
  const handleOpenChange: React.ComponentProps<
    typeof Dialog.Root
  >["onOpenChange"] = (open, eventDetails) => {
    if (
      !dismissible &&
      !open &&
      (eventDetails.reason === "escape-key" ||
        eventDetails.reason === "outside-press")
    ) {
      eventDetails.cancel();
      return;
    }

    onOpenChange(open);
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={handleOpenChange}
      modal
      disablePointerDismissal={!dismissible}
    >
      <Dialog.Portal container={portalHost}>
        <Dialog.Backdrop className="fixed inset-0 z-[var(--z-layer-dialog)] bg-surface-scrim" />
        <Dialog.Viewport className="fixed inset-0 z-[var(--z-layer-dialog)] flex items-center justify-center max-[599px]:items-end">
          <Dialog.Popup
            aria-modal="true"
            className={cn(modalStyles({ size: resolvedSize }))}
          >
            <div className="shrink-0 flex flex-col gap-(--spacing-6)">
              <div className="flex items-center justify-between">
                <Dialog.Title className={cn(titleStyles())}>
                  {title}
                </Dialog.Title>
                {dismissible && (
                  <Dialog.Close
                    render={
                      <IconButton
                        icon="x-close"
                        aria-label="Close"
                        variant="ghost"
                        size="sm"
                      />
                    }
                  />
                )}
              </div>
              {description && (
                <Dialog.Description className={cn(descriptionStyles())}>
                  {description}
                </Dialog.Description>
              )}
            </div>

            {children && (
              <div className="min-h-0 mt-(--spacing-20) overflow-y-auto">
                {children}
              </div>
            )}

            {footer && (
              <div className={cn(footerStyles({ size: resolvedSize }))}>
                {footer}
              </div>
            )}
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export { Modal };
export type { ModalSize };
